import { useState } from 'react'
import type { ChangeEvent } from 'react'
import { Button } from '../../../shared/ui'
import { addSuppressions } from '../../../lib/outreach/api'
import { parseSuppressionCsv } from '../../../lib/outreach/csv'
import type { SuppressionCsv } from '../../../lib/outreach/csv'
import { toast } from '../../../lib/toast'
import { ErrorLine, reportError } from './shared'

const PREVIEW_ROWS = 10

function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(new Error('Could not read that file.'))
    reader.readAsText(file)
  })
}

export function resultSentence(r: { added: number; skipped: number; invalid: number }): string {
  const parts = [`Added ${r.added}.`]
  if (r.skipped) parts.push(`${r.skipped} already on the list.`)
  if (r.invalid) parts.push(`${r.invalid} not valid.`)
  return parts.join(' ')
}

export function SuppressionImport({ onImported }: { onImported: () => void }) {
  const [parsed, setParsed] = useState<SuppressionCsv | null>(null)
  const [fileName, setFileName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setError('')
    setMessage('')
    setParsed(null)
    if (!file) return
    try {
      const text = await readText(file)
      setFileName(file.name)
      setParsed(parseSuppressionCsv(text))
    } catch (err) {
      setError(reportError(err))
    }
  }

  const confirm = async () => {
    if (!parsed || parsed.rows.length === 0) return
    setBusy(true)
    setError('')
    try {
      const r = await addSuppressions(parsed.rows)
      setMessage(resultSentence(r))
      toast(resultSentence(r), 'success')
      setParsed(null)
      onImported()
    } catch (err) {
      setError(reportError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="os-card">
      <div className="settings-row">
        <label className="settings-label" htmlFor="sup-file">
          Import CSV
        </label>
        <input id="sup-file" type="file" accept=".csv,.txt" onChange={(e) => void pick(e)} />
        <div className="settings-hint">
          One value per line, or columns named kind, value and reason. The kind is guessed when it
          is missing.
        </div>
      </div>
      <ErrorLine>{error}</ErrorLine>
      {message && <div className="os-ok">{message}</div>}
      {parsed && (
        <div role="region" aria-label="Import preview">
          <div className="os-meta">
            <span>{fileName}</span>
            <span>{parsed.rows.length} valid</span>
            <span>{parsed.invalid.length} invalid</span>
            <span>{parsed.duplicates} duplicates in the file</span>
          </div>
          {parsed.rows.length > 0 && (
            <table className="os-table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Value</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {parsed.rows.slice(0, PREVIEW_ROWS).map((r, i) => (
                  <tr key={i}>
                    <td>{r.kind}</td>
                    <td>{r.value}</td>
                    <td>{r.reason ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {parsed.rows.length > PREVIEW_ROWS && (
            <div className="settings-hint">
              Showing the first {PREVIEW_ROWS} of {parsed.rows.length}.
            </div>
          )}
          {parsed.invalid.length > 0 && (
            <div className="os-warn">
              Skipped lines: {parsed.invalid.slice(0, PREVIEW_ROWS).join(' | ')}
              {parsed.invalid.length > PREVIEW_ROWS
                ? ` and ${parsed.invalid.length - PREVIEW_ROWS} more`
                : ''}
            </div>
          )}
          <div className="os-bar">
            <Button disabled={busy || parsed.rows.length === 0} onClick={() => void confirm()}>
              {busy ? 'Adding...' : `Add ${parsed.rows.length} to the list`}
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => setParsed(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
